import {
  AlertTriangle,
  BarChart3,
  CalendarRange,
  Download,
  FileDown,
  ImageDown,
  LoaderCircle,
  MailCheck,
  RefreshCw,
  Table2,
} from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { ReportDataWarnings } from '../../components/ReportDataWarnings';
import { requireFirebaseFunctions } from '../../lib/firebase';
import { formatReportGeneration, OUTPUT_REPORT_CALLABLE_OPTIONS } from '../../lib/generated-report';
import {
  outputReportMonths,
  outputReportYears,
  type ProcessingAvailability,
} from '../../lib/processing-data';
import { svgElementMarkupForExport, svgMarkupDataUrl } from '../../lib/svg-export';
import {
  createPecSocialSubreasonWorkbook,
  pecSocialSubreasonWorkbookFileName,
  type PecAnnualSocialReasonKey,
  type PecOperationalRowKey,
  type PecEntryChannelKey,
  type PecSocialReasonKey,
  type PecTopicKey,
  type PecSocialSubreasonAnalysis,
} from '../../lib/pec-social-subreason';

const MONTHS = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',
];
const ROW_COLORS: Record<PecOperationalRowKey, string> = {
  incoming: '#e8b1ea',
  closed: '#ec3672',
  'agency-backlog': '#fff34f',
  working: '#173249',
};
const CHANNEL_COLORS: Record<PecEntryChannelKey, string> = {
  web: '#df2b91',
  facebook: '#b43fc9',
  linkedin: '#45a7dc',
  x: '#4474df',
};
const TOPIC_COLORS: Record<PecTopicKey, string> = {
  'general-information': '#ff1f6d',
  problems: '#2d3047',
  'infratel-information': '#e8b1ea',
  complaints: '#45a7dc',
};
const SOCIAL_REASON_COLORS: Record<PecSocialReasonKey, string> = {
  delivery: '#df2b91',
  coverage: '#bf2dcb',
  assurance: '#4fa7d5',
  'other-general-information': '#4776df',
  'other-problems': '#806edc',
  saleability: '#d5466f',
  creation: '#e26da7',
  operators: '#d45ad2',
};
const ANNUAL_SOCIAL_REASON_COLORS: Record<PecAnnualSocialReasonKey, string> = {
  other: '#df2b91',
  coverage: '#bf2dcb',
  delivery: '#4fa7d5',
  assurance: '#4776df',
  saleability: '#806edc',
};

type LoadStatus = 'idle' | 'loading' | 'ready' | 'error';

function formatNumber(value: number): string {
  return new Intl.NumberFormat('it-IT').format(value);
}

function defaultMonthForYear(year: number): number {
  const now = new Date();
  return year === now.getFullYear() ? Math.max(1, now.getMonth()) : 12;
}

function errorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
    return error.message.replace(/^Firebase:\s*/i, '').replace(/\s*\([^)]*\)\.?$/, '');
  }
  return 'Non è stato possibile generare il report Calcolo social subreason PEC.';
}

function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function polarPoint(cx: number, cy: number, radius: number, angle: number): { x: number; y: number } {
  const radians = ((angle - 90) * Math.PI) / 180;
  return { x: cx + radius * Math.cos(radians), y: cy + radius * Math.sin(radians) };
}

function piePath(cx: number, cy: number, radius: number, start: number, end: number): string {
  const startPoint = polarPoint(cx, cy, radius, end);
  const endPoint = polarPoint(cx, cy, radius, start);
  const largeArc = end - start <= 180 ? 0 : 1;
  return `M ${cx} ${cy} L ${startPoint.x} ${startPoint.y} A ${radius} ${radius} 0 ${largeArc} 0 ${endPoint.x} ${endPoint.y} Z`;
}

function demoAnalysis(): PecSocialSubreasonAnalysis {
  return {
    selectedYear: 2026,
    selectedMonth: 8,
    periodLabel: 'Agosto 2026',
    totalIncoming: 6447,
    pecCases: 6,
    closedCases: 6441,
    rows: [
      { key: 'incoming', label: 'In entrata', value: 0 },
      { key: 'closed', label: 'Chiuse', value: 6441 },
      { key: 'agency-backlog', label: 'Backlog agenzia', value: 0 },
      { key: 'working', label: 'In lavorazione', value: 6 },
    ],
    territorialRows: [
      { key: 'nord-est', label: 'Nord - Est', value: 4 },
      { key: 'centro', label: 'Centro', value: 1 },
      { key: 'nord-ovest', label: 'Nord - Ovest', value: 1 },
      { key: 'sud', label: 'Sud', value: 0 },
    ],
    territorialResolvedCases: 6,
    territorialIssues: [],
    channelCutoffDay: 12,
    channelRows: [
      { key: 'web', label: 'Web', closed: 5802, working: 291, total: 6093 },
      { key: 'facebook', label: 'Facebook', closed: 263, working: 0, total: 263 },
      { key: 'linkedin', label: 'LinkedIn', closed: 69, working: 0, total: 69 },
      { key: 'x', label: 'X', closed: 22, working: 0, total: 22 },
    ],
    channelTotals: { closed: 6156, working: 291, total: 6447 },
    topicRows: [
      { key: 'general-information', label: 'Informazioni generali', value: 2195 },
      { key: 'problems', label: 'Problemi', value: 3738 },
      { key: 'infratel-information', label: 'Informazioni Infratel', value: 1 },
      { key: 'complaints', label: 'Reclami', value: 140 },
    ],
    topicTotal: 6074,
    socialReasonRows: [
      { key: 'delivery', label: 'Delivery', value: 1486 },
      { key: 'coverage', label: 'Copertura', value: 1396 },
      { key: 'assurance', label: 'Assurance', value: 1251 },
      { key: 'other-general-information', label: 'Altro - Info generali', value: 783 },
      { key: 'other-problems', label: 'Altro - Problemi', value: 591 },
      { key: 'saleability', label: 'Vendibilità', value: 371 },
      { key: 'creation', label: 'Creation', value: 103 },
      { key: 'operators', label: 'Operatori', value: 85 },
    ],
    socialReasonTotal: 6066,
    annualSocialReasonRows: [
      { key: 'other', label: 'Altro', value: 28451 },
      { key: 'coverage', label: 'Copertura', value: 15863 },
      { key: 'delivery', label: 'Delivery', value: 14876 },
      { key: 'assurance', label: 'Assurance', value: 11515 },
      { key: 'saleability', label: 'Vendibilità', value: 6698 },
    ],
    annualSocialReasonTotal: 77403,
    annualSocialReasonStartDate: '2025-09-01',
    annualSocialReasonEndDate: '2026-09-30',
    annualSocialReasonPeriodLabel: '1 settembre 2025 – 30 settembre 2026',
    annualMasterFiles: [
      {
        id: 'demo-master-2025',
        name: '2025.xlsx',
        path: 'Export / Export annuale / 2025.xlsx',
        modifiedTime: '2026-09-22T18:47:00.000Z',
        webViewLink: null,
      },
      {
        id: 'demo-master-2026',
        name: '2026.xlsx',
        path: 'Export / Export annuale / 2026.xlsx',
        modifiedTime: '2026-09-22T18:47:00.000Z',
        webViewLink: null,
      },
    ],
    masterFile: {
      id: 'demo-master',
      name: '2026.xlsx',
      path: 'Export / Export annuale / 2026.xlsx',
      modifiedTime: '2026-09-22T18:47:00.000Z',
      webViewLink: null,
    },
    mailFile: {
      id: 'demo-mail',
      name: 'Report 12.08.2026.xlsx',
      path: 'Report ricevuti / 2026 / 08 - Agosto / Report 12.08.2026.xlsx',
      modifiedTime: '2026-08-12T18:47:00.000Z',
      webViewLink: null,
      referenceDate: '2026-08-12',
    },
    worksheetName: 'Report Segnalazioni Utenti Soci',
  };
}

const PecOperationalChart = forwardRef<SVGSVGElement, { analysis: PecSocialSubreasonAnalysis }>(function PecOperationalChart({ analysis }, ref) {
  const positiveRows = analysis.rows.filter(({ value }) => value > 0);
  const total = positiveRows.reduce((sum, row) => sum + row.value, 0);
  const angles = positiveRows.map((row) => (row.value / Math.max(total, 1)) * 360);
  const segments = positiveRows.map((row, index) => {
    const start = angles.slice(0, index).reduce((sum, angle) => sum + angle, 0);
    const end = start + (row.value / Math.max(total, 1)) * 360;
    return { row, start, end };
  });

  return (
    <svg ref={ref} className="pec-chart-svg" viewBox="0 0 960 500" role="img" aria-labelledby="pec-chart-title pec-chart-description" xmlns="http://www.w3.org/2000/svg">
      <title id="pec-chart-title">Stato operativo case · {analysis.periodLabel}</title>
      <desc id="pec-chart-description">Distribuzione dei case mensili tra chiuse, in lavorazione, in entrata e backlog agenzia.</desc>
      <rect width="960" height="500" rx="12" fill="#ffffff" />
      <text data-export-title="true" x="42" y="43" fill="#173249" fontFamily="Inter, Arial, sans-serif" fontSize="18" fontWeight="700">Stato operativo case</text>
      <text data-export-title="true" x="42" y="67" fill="#7b8a96" fontFamily="Inter, Arial, sans-serif" fontSize="12">{analysis.periodLabel} · {formatNumber(analysis.totalIncoming)} casi totali</text>

      {total === 0 ? (
        <g>
          <circle cx="290" cy="275" r="150" fill="#f2f5f7" stroke="#d9e1e6" strokeWidth="2" />
          <text x="290" y="282" textAnchor="middle" fill="#86949f" fontFamily="Inter, Arial, sans-serif" fontSize="16" fontWeight="700">Nessun caso</text>
        </g>
      ) : segments.length === 1 ? (
        <circle cx="290" cy="275" r="150" fill={ROW_COLORS[segments[0].row.key]}>
          <title>{segments[0].row.label}: {formatNumber(segments[0].row.value)}</title>
        </circle>
      ) : segments.map(({ row, start, end }) => (
        <path key={row.key} d={piePath(290, 275, 150, start, end)} fill={ROW_COLORS[row.key]} stroke="#ffffff" strokeWidth="2">
          <title>{row.label}: {formatNumber(row.value)}</title>
        </path>
      ))}

      <g transform="translate(535 145)">
        {analysis.rows.map((row, index) => {
          const percentage = analysis.totalIncoming > 0 ? (row.value / analysis.totalIncoming) * 100 : 0;
          return (
            <g key={row.key} transform={`translate(0 ${index * 72})`}>
              <rect width="16" height="16" y="2" rx="3" fill={ROW_COLORS[row.key]} stroke={row.key === 'agency-backlog' ? '#d6cb37' : 'none'} />
              <text x="28" y="16" fill="#263c4d" fontFamily="Inter, Arial, sans-serif" fontSize="14" fontWeight="700">{row.label}</text>
              <text x="28" y="39" fill="#667987" fontFamily="Inter, Arial, sans-serif" fontSize="12">{formatNumber(row.value)} · {percentage.toLocaleString('it-IT', { maximumFractionDigits: 1 })}%</text>
            </g>
          );
        })}
      </g>
    </svg>
  );
});

const PecTerritorialChart = forwardRef<SVGSVGElement, { analysis: PecSocialSubreasonAnalysis }>(function PecTerritorialChart({ analysis }, ref) {
  const chartRows = analysis.territorialRows;
  const maximum = Math.max(1, ...chartRows.map(({ value }) => value));
  const yMaximum = Math.max(4, Math.ceil(maximum / 2) * 2);
  const plot = { left: 82, top: 92, right: 920, bottom: 406 };
  const plotHeight = plot.bottom - plot.top;
  const step = (plot.right - plot.left) / Math.max(chartRows.length, 1);
  const barWidth = Math.min(94, step * 0.48);

  return (
    <svg ref={ref} className="pec-chart-svg" viewBox="0 0 960 500" role="img" aria-labelledby="pec-territorial-chart-title pec-territorial-chart-description" xmlns="http://www.w3.org/2000/svg">
      <title id="pec-territorial-chart-title">Distribuzione case PEC per area territoriale · {analysis.periodLabel}</title>
      <desc id="pec-territorial-chart-description">Numero di case PEC in lavorazione nelle aree Nord-Est, Centro, Nord-Ovest e Sud.</desc>
      <rect width="960" height="500" rx="12" fill="#ffffff" />
      <text data-export-title="true" x="42" y="43" fill="#173249" fontFamily="Inter, Arial, sans-serif" fontSize="18" fontWeight="700">Distribuzione PEC per area territoriale</text>
      <text data-export-title="true" x="42" y="67" fill="#7b8a96" fontFamily="Inter, Arial, sans-serif" fontSize="12">{analysis.periodLabel} · {formatNumber(analysis.territorialResolvedCases)} case distribuite</text>
      {Array.from({ length: 5 }, (_, index) => {
        const value = (yMaximum / 4) * index;
        const y = plot.bottom - (plotHeight / 4) * index;
        return (
          <g key={value}>
            <line x1={plot.left} x2={plot.right} y1={y} y2={y} stroke="#d8dde1" strokeWidth="1" />
            <text x={plot.left - 12} y={y + 4} textAnchor="end" fill="#526675" fontFamily="Inter, Arial, sans-serif" fontSize="12">{formatNumber(value)}</text>
          </g>
        );
      })}
      <line x1={plot.left} x2={plot.right} y1={plot.bottom} y2={plot.bottom} stroke="#263c4d" strokeWidth="1.5" />
      {chartRows.map((row, index) => {
        const x = plot.left + step * index + (step - barWidth) / 2;
        const height = (row.value / yMaximum) * plotHeight;
        const y = plot.bottom - height;
        return (
          <g key={row.key}>
            <rect x={x} y={y} width={barWidth} height={height} rx="5" fill="#ec3672">
              <title>{row.label}: {formatNumber(row.value)}</title>
            </rect>
            <text x={x + barWidth / 2} y={Math.max(plot.top + 12, y - 10)} textAnchor="middle" fill="#263c4d" fontFamily="Inter, Arial, sans-serif" fontSize="14" fontWeight="700">{formatNumber(row.value)}</text>
            <text x={x + barWidth / 2} y={plot.bottom + 32} textAnchor="middle" fill="#263c4d" fontFamily="Inter, Arial, sans-serif" fontSize="13" fontWeight="600">{row.label}</text>
          </g>
        );
      })}
    </svg>
  );
});

const PecChannelPieChart = forwardRef<SVGSVGElement, { analysis: PecSocialSubreasonAnalysis }>(function PecChannelPieChart({ analysis }, ref) {
  const positiveRows = analysis.channelRows.filter(({ total }) => total > 0);
  const total = Math.max(analysis.channelTotals.total, 1);
  const segments = positiveRows.map((row, index) => {
    const start = positiveRows.slice(0, index).reduce((sum, item) => sum + (item.total / total) * 360, 0);
    return { row, start, end: start + (row.total / total) * 360 };
  });

  return (
    <svg ref={ref} className="pec-channel-chart-svg" viewBox="0 0 800 500" role="img" aria-labelledby="pec-channel-pie-title pec-channel-pie-description" xmlns="http://www.w3.org/2000/svg">
      <title id="pec-channel-pie-title">Distribuzione dei canali di ingresso · {analysis.periodLabel}</title>
      <desc id="pec-channel-pie-description">Distribuzione percentuale dei casi entrati da Web, Facebook, LinkedIn e X.</desc>
      <rect width="800" height="500" rx="12" fill="#ffffff" />
      <text data-export-title="true" x="36" y="42" fill="#173249" fontFamily="Inter, Arial, sans-serif" fontSize="18" fontWeight="700">Canali di ingresso</text>
      <text data-export-title="true" x="36" y="66" fill="#7b8a96" fontFamily="Inter, Arial, sans-serif" fontSize="12">{analysis.periodLabel} · {formatNumber(analysis.channelTotals.total)} casi</text>
      {positiveRows.length === 0 ? (
        <circle cx="270" cy="270" r="150" fill="#f2f5f7" stroke="#d9e1e6" strokeWidth="2" />
      ) : segments.length === 1 ? (
        <circle cx="270" cy="270" r="150" fill={CHANNEL_COLORS[segments[0].row.key]} />
      ) : segments.map(({ row, start, end }) => (
        <path key={row.key} d={piePath(270, 270, 150, start, end)} fill={CHANNEL_COLORS[row.key]} stroke="#ffffff" strokeWidth="2">
          <title>{row.label}: {formatNumber(row.total)}</title>
        </path>
      ))}
      <g transform="translate(500 150)">
        {analysis.channelRows.map((row, index) => (
          <g key={row.key} transform={`translate(0 ${index * 68})`}>
            <circle cx="8" cy="8" r="7" fill={CHANNEL_COLORS[row.key]} />
            <text x="28" y="13" fill="#263c4d" fontFamily="Inter, Arial, sans-serif" fontSize="14" fontWeight="700">{row.label}</text>
            <text x="28" y="35" fill="#667987" fontFamily="Inter, Arial, sans-serif" fontSize="12">{formatNumber(row.total)} · {((row.total / total) * 100).toLocaleString('it-IT', { maximumFractionDigits: 1 })}%</text>
          </g>
        ))}
      </g>
    </svg>
  );
});

const PecChannelBarChart = forwardRef<SVGSVGElement, { analysis: PecSocialSubreasonAnalysis }>(function PecChannelBarChart({ analysis }, ref) {
  const maximum = Math.max(1, ...analysis.channelRows.flatMap(({ closed, working }) => [closed, working]));
  const magnitude = 10 ** Math.floor(Math.log10(maximum));
  const yMaximum = Math.max(4, Math.ceil(maximum / magnitude) * magnitude);
  const plot = { left: 76, top: 92, right: 766, bottom: 406 };
  const plotHeight = plot.bottom - plot.top;
  const step = (plot.right - plot.left) / Math.max(analysis.channelRows.length, 1);
  const barWidth = Math.min(42, step * 0.25);

  return (
    <svg ref={ref} className="pec-channel-chart-svg" viewBox="0 0 800 500" role="img" aria-labelledby="pec-channel-bars-title pec-channel-bars-description" xmlns="http://www.w3.org/2000/svg">
      <title id="pec-channel-bars-title">Casi chiusi e in lavorazione per canale · {analysis.periodLabel}</title>
      <desc id="pec-channel-bars-description">Confronto tra casi chiusi e in lavorazione per Web, Facebook, LinkedIn e X.</desc>
      <rect width="800" height="500" rx="12" fill="#ffffff" />
      <text data-export-title="true" x="36" y="42" fill="#173249" fontFamily="Inter, Arial, sans-serif" fontSize="18" fontWeight="700">Stato per canale</text>
      <text data-export-title="true" x="36" y="66" fill="#7b8a96" fontFamily="Inter, Arial, sans-serif" fontSize="12">Casi chiusi e in lavorazione · {analysis.periodLabel}</text>
      <g transform="translate(500 38)">
        <rect width="12" height="12" rx="2" fill="#d84572" /><text x="18" y="11" fill="#263c4d" fontFamily="Inter, Arial, sans-serif" fontSize="11">Casi chiusi</text>
        <rect x="105" width="12" height="12" rx="2" fill="#bfc2c4" /><text x="123" y="11" fill="#263c4d" fontFamily="Inter, Arial, sans-serif" fontSize="11">In lavorazione</text>
      </g>
      {Array.from({ length: 4 }, (_, index) => {
        const value = (yMaximum / 3) * index;
        const y = plot.bottom - (plotHeight / 3) * index;
        return (
          <g key={index}>
            <line x1={plot.left} x2={plot.right} y1={y} y2={y} stroke="#d8dde1" strokeWidth="1" />
            <text x={plot.left - 12} y={y + 4} textAnchor="end" fill="#526675" fontFamily="Inter, Arial, sans-serif" fontSize="11">{formatNumber(Math.round(value))}</text>
          </g>
        );
      })}
      <line x1={plot.left} x2={plot.right} y1={plot.bottom} y2={plot.bottom} stroke="#263c4d" strokeWidth="1.5" />
      {analysis.channelRows.map((row, index) => {
        const center = plot.left + step * index + step / 2;
        const closedHeight = (row.closed / yMaximum) * plotHeight;
        const workingHeight = (row.working / yMaximum) * plotHeight;
        return (
          <g key={row.key}>
            <rect x={center - barWidth - 3} y={plot.bottom - closedHeight} width={barWidth} height={closedHeight} rx="3" fill="#d84572"><title>{row.label}, chiusi: {formatNumber(row.closed)}</title></rect>
            <rect x={center + 3} y={plot.bottom - workingHeight} width={barWidth} height={workingHeight} rx="3" fill="#bfc2c4"><title>{row.label}, in lavorazione: {formatNumber(row.working)}</title></rect>
            <text x={center - barWidth / 2 - 3} y={Math.max(plot.top + 12, plot.bottom - closedHeight - 8)} textAnchor="middle" fill="#d84572" fontFamily="Inter, Arial, sans-serif" fontSize="11" fontWeight="700">{formatNumber(row.closed)}</text>
            <text x={center + barWidth / 2 + 3} y={Math.max(plot.top + 12, plot.bottom - workingHeight - 8)} textAnchor="middle" fill="#92979a" fontFamily="Inter, Arial, sans-serif" fontSize="11" fontWeight="700">{formatNumber(row.working)}</text>
            <text x={center} y={plot.bottom + 31} textAnchor="middle" fill="#263c4d" fontFamily="Inter, Arial, sans-serif" fontSize="12" fontWeight="700">{row.label}</text>
          </g>
        );
      })}
    </svg>
  );
});

const PecTopicChart = forwardRef<SVGSVGElement, { analysis: PecSocialSubreasonAnalysis }>(function PecTopicChart({ analysis }, ref) {
  const positiveRows = analysis.topicRows.filter(({ value }) => value > 0);
  const total = Math.max(analysis.topicTotal, 1);
  const segments = positiveRows.map((row, index) => {
    const start = positiveRows.slice(0, index).reduce((sum, item) => sum + (item.value / total) * 360, 0);
    return { row, start, end: start + (row.value / total) * 360 };
  });

  return (
    <svg ref={ref} className="pec-topic-chart-svg" viewBox="0 0 800 500" role="img" aria-labelledby="pec-topic-chart-title pec-topic-chart-description" xmlns="http://www.w3.org/2000/svg">
      <title id="pec-topic-chart-title">Distribuzione case per argomento · {analysis.periodLabel}</title>
      <desc id="pec-topic-chart-description">Distribuzione dei case mensili tra Informazioni generali, Problemi, Informazioni Infratel e Reclami.</desc>
      <rect width="800" height="500" rx="12" fill="#ffffff" />
      <text data-export-title="true" x="36" y="42" fill="#173249" fontFamily="Inter, Arial, sans-serif" fontSize="18" fontWeight="700">Distribuzione case per tipologia</text>
      <text data-export-title="true" x="36" y="66" fill="#7b8a96" fontFamily="Inter, Arial, sans-serif" fontSize="12">{analysis.periodLabel} · {formatNumber(analysis.topicTotal)} case classificate</text>
      {positiveRows.length === 0 ? (
        <g>
          <circle cx="280" cy="275" r="150" fill="#f2f5f7" stroke="#d9e1e6" strokeWidth="2" />
          <text x="280" y="282" textAnchor="middle" fill="#86949f" fontFamily="Inter, Arial, sans-serif" fontSize="16" fontWeight="700">Nessun caso</text>
        </g>
      ) : segments.length === 1 ? (
        <circle cx="280" cy="275" r="150" fill={TOPIC_COLORS[segments[0].row.key]} />
      ) : segments.map(({ row, start, end }) => (
        <path key={row.key} d={piePath(280, 275, 150, start, end)} fill={TOPIC_COLORS[row.key]} stroke="#ffffff" strokeWidth="2">
          <title>{row.label}: {formatNumber(row.value)}</title>
        </path>
      ))}
      <g transform="translate(500 142)">
        {analysis.topicRows.map((row, index) => (
          <g key={row.key} transform={`translate(0 ${index * 72})`}>
            <circle cx="8" cy="8" r="7" fill={TOPIC_COLORS[row.key]} />
            <text x="28" y="13" fill="#263c4d" fontFamily="Inter, Arial, sans-serif" fontSize="14" fontWeight="700">{row.label}</text>
            <text x="28" y="36" fill="#667987" fontFamily="Inter, Arial, sans-serif" fontSize="12">{formatNumber(row.value)} · {((row.value / total) * 100).toLocaleString('it-IT', { maximumFractionDigits: 1 })}%</text>
          </g>
        ))}
      </g>
    </svg>
  );
});

const PecSocialReasonBarChart = forwardRef<SVGSVGElement, { analysis: PecSocialSubreasonAnalysis }>(function PecSocialReasonBarChart({ analysis }, ref) {
  const maximum = Math.max(1, ...analysis.socialReasonRows.map(({ value }) => value));
  const magnitude = 10 ** Math.floor(Math.log10(maximum));
  const yMaximum = Math.max(4, Math.ceil(maximum / magnitude) * magnitude);
  const plot = { left: 72, top: 78, right: 812, bottom: 390 };
  const plotHeight = plot.bottom - plot.top;
  const step = (plot.right - plot.left) / Math.max(analysis.socialReasonRows.length, 1);
  const barWidth = Math.min(58, step * 0.62);

  return (
    <svg ref={ref} className="pec-social-reason-chart-svg" viewBox="0 0 860 530" role="img" aria-labelledby="pec-social-reason-bar-title pec-social-reason-bar-description" xmlns="http://www.w3.org/2000/svg">
      <title id="pec-social-reason-bar-title">Richieste per Social Reason · {analysis.periodLabel}</title>
      <desc id="pec-social-reason-bar-description">Confronto dei volumi mensili per Social Reason, con Altro distinto per Argomento.</desc>
      <rect width="860" height="530" rx="12" fill="#ffffff" />
      <text data-export-title="true" x="36" y="40" fill="#173249" fontFamily="Inter, Arial, sans-serif" fontSize="18" fontWeight="700">Richieste per Social Reason</text>
      <text data-export-title="true" x="36" y="63" fill="#7b8a96" fontFamily="Inter, Arial, sans-serif" fontSize="12">{analysis.periodLabel} · {formatNumber(analysis.socialReasonTotal)} richieste classificate</text>
      {Array.from({ length: 5 }, (_, index) => {
        const value = (yMaximum / 4) * index;
        const y = plot.bottom - (plotHeight / 4) * index;
        return (
          <g key={index}>
            <line x1={plot.left} x2={plot.right} y1={y} y2={y} stroke="#d8dde1" strokeWidth="1" />
            <text x={plot.left - 12} y={y + 4} textAnchor="end" fill="#526675" fontFamily="Inter, Arial, sans-serif" fontSize="11">{formatNumber(Math.round(value))}</text>
          </g>
        );
      })}
      <line x1={plot.left} x2={plot.right} y1={plot.bottom} y2={plot.bottom} stroke="#263c4d" strokeWidth="1.5" />
      {analysis.socialReasonRows.map((row, index) => {
        const center = plot.left + step * index + step / 2;
        const height = (row.value / yMaximum) * plotHeight;
        const y = plot.bottom - height;
        return (
          <g key={row.key}>
            <rect x={center - barWidth / 2} y={y} width={barWidth} height={height} rx="4" fill="#bfc1c6">
              <title>{row.label}: {formatNumber(row.value)}</title>
            </rect>
            <text x={center} y={Math.max(plot.top + 12, y - 8)} textAnchor="middle" fill="#263c4d" fontFamily="Inter, Arial, sans-serif" fontSize="11" fontWeight="700">{formatNumber(row.value)}</text>
            <text transform={`translate(${center + 2} ${plot.bottom + 22}) rotate(-43)`} textAnchor="end" fill="#263c4d" fontFamily="Inter, Arial, sans-serif" fontSize="11" fontWeight="600">{row.label}</text>
          </g>
        );
      })}
    </svg>
  );
});

const PecSocialReasonPieChart = forwardRef<SVGSVGElement, { analysis: PecSocialSubreasonAnalysis }>(function PecSocialReasonPieChart({ analysis }, ref) {
  const positiveRows = analysis.socialReasonRows.filter(({ value }) => value > 0);
  const total = Math.max(analysis.socialReasonTotal, 1);
  const segments = positiveRows.map((row, index) => {
    const start = positiveRows.slice(0, index).reduce((sum, item) => sum + (item.value / total) * 360, 0);
    return { row, start, end: start + (row.value / total) * 360 };
  });

  return (
    <svg ref={ref} className="pec-social-reason-chart-svg" viewBox="0 0 860 530" role="img" aria-labelledby="pec-social-reason-pie-title pec-social-reason-pie-description" xmlns="http://www.w3.org/2000/svg">
      <title id="pec-social-reason-pie-title">Incidenza dei Social Reason · {analysis.periodLabel}</title>
      <desc id="pec-social-reason-pie-description">Distribuzione percentuale delle richieste mensili per Social Reason.</desc>
      <rect width="860" height="530" rx="12" fill="#ffffff" />
      <text data-export-title="true" x="36" y="40" fill="#173249" fontFamily="Inter, Arial, sans-serif" fontSize="18" fontWeight="700">Distribuzione Social Reason</text>
      <text data-export-title="true" x="36" y="63" fill="#7b8a96" fontFamily="Inter, Arial, sans-serif" fontSize="12">{analysis.periodLabel} · {formatNumber(analysis.socialReasonTotal)} richieste classificate</text>
      {positiveRows.length === 0 ? (
        <circle cx="260" cy="285" r="155" fill="#f2f5f7" stroke="#d9e1e6" strokeWidth="2" />
      ) : segments.length === 1 ? (
        <circle cx="260" cy="285" r="155" fill={SOCIAL_REASON_COLORS[segments[0].row.key]} />
      ) : segments.map(({ row, start, end }) => (
        <path key={row.key} d={piePath(260, 285, 155, start, end)} fill={SOCIAL_REASON_COLORS[row.key]} stroke="#ffffff" strokeWidth="2">
          <title>{row.label}: {formatNumber(row.value)}</title>
        </path>
      ))}
      <g transform="translate(475 98)">
        {analysis.socialReasonRows.map((row, index) => (
          <g key={row.key} transform={`translate(0 ${index * 50})`}>
            <circle cx="7" cy="7" r="6" fill={SOCIAL_REASON_COLORS[row.key]} />
            <text x="24" y="11" fill="#263c4d" fontFamily="Inter, Arial, sans-serif" fontSize="12" fontWeight="700">{row.label}</text>
            <text x="24" y="29" fill="#667987" fontFamily="Inter, Arial, sans-serif" fontSize="10">{formatNumber(row.value)} · {((row.value / total) * 100).toLocaleString('it-IT', { maximumFractionDigits: 1 })}%</text>
          </g>
        ))}
      </g>
    </svg>
  );
});

const PecAnnualSocialReasonBarChart = forwardRef<SVGSVGElement, { analysis: PecSocialSubreasonAnalysis }>(function PecAnnualSocialReasonBarChart({ analysis }, ref) {
  const maximum = Math.max(1, ...analysis.annualSocialReasonRows.map(({ value }) => value));
  const magnitude = 10 ** Math.floor(Math.log10(maximum));
  const yMaximum = Math.max(4, Math.ceil(maximum / magnitude) * magnitude);
  const plot = { left: 78, top: 78, right: 812, bottom: 420 };
  const plotHeight = plot.bottom - plot.top;
  const step = (plot.right - plot.left) / Math.max(analysis.annualSocialReasonRows.length, 1);
  const barWidth = Math.min(94, step * 0.66);

  return (
    <svg ref={ref} className="pec-social-reason-chart-svg" viewBox="0 0 860 530" role="img" aria-labelledby="pec-annual-social-reason-bar-title pec-annual-social-reason-bar-description" xmlns="http://www.w3.org/2000/svg">
      <title id="pec-annual-social-reason-bar-title">Distribuzione annuale dei Social Reason · {analysis.annualSocialReasonPeriodLabel}</title>
      <desc id="pec-annual-social-reason-bar-description">Confronto dei cinque Social Reason nel periodo annuale mobile selezionato.</desc>
      <rect width="860" height="530" rx="12" fill="#ffffff" />
      <text data-export-title="true" x="36" y="40" fill="#173249" fontFamily="Inter, Arial, sans-serif" fontSize="18" fontWeight="700">Distribuzione case per tipologia</text>
      <text data-export-title="true" x="36" y="63" fill="#7b8a96" fontFamily="Inter, Arial, sans-serif" fontSize="12">{analysis.annualSocialReasonPeriodLabel} · {formatNumber(analysis.annualSocialReasonTotal)} casi</text>
      {Array.from({ length: 4 }, (_, index) => {
        const value = (yMaximum / 3) * index;
        const y = plot.bottom - (plotHeight / 3) * index;
        return (
          <g key={index}>
            <line x1={plot.left} x2={plot.right} y1={y} y2={y} stroke="#d8dde1" strokeWidth="1" />
            <text x={plot.left - 12} y={y + 4} textAnchor="end" fill="#526675" fontFamily="Inter, Arial, sans-serif" fontSize="11">{formatNumber(Math.round(value))}</text>
          </g>
        );
      })}
      <line x1={plot.left} x2={plot.right} y1={plot.bottom} y2={plot.bottom} stroke="#263c4d" strokeWidth="1.5" />
      {analysis.annualSocialReasonRows.map((row, index) => {
        const center = plot.left + step * index + step / 2;
        const height = (row.value / yMaximum) * plotHeight;
        const y = plot.bottom - height;
        return (
          <g key={row.key}>
            <rect x={center - barWidth / 2} y={y} width={barWidth} height={height} rx="4" fill="#ff1f6d">
              <title>{row.label}: {formatNumber(row.value)}</title>
            </rect>
            <text x={center} y={Math.max(plot.top + 12, y - 9)} textAnchor="middle" fill="#c61d55" fontFamily="Inter, Arial, sans-serif" fontSize="12" fontWeight="700">{formatNumber(row.value)}</text>
            <text x={center} y={plot.bottom + 31} textAnchor="middle" fill="#263c4d" fontFamily="Inter, Arial, sans-serif" fontSize="12" fontWeight="700">{row.label}</text>
          </g>
        );
      })}
    </svg>
  );
});

const PecAnnualSocialReasonPieChart = forwardRef<SVGSVGElement, { analysis: PecSocialSubreasonAnalysis }>(function PecAnnualSocialReasonPieChart({ analysis }, ref) {
  const positiveRows = analysis.annualSocialReasonRows.filter(({ value }) => value > 0);
  const total = Math.max(analysis.annualSocialReasonTotal, 1);
  const segments = positiveRows.map((row, index) => {
    const start = positiveRows.slice(0, index).reduce((sum, item) => sum + (item.value / total) * 360, 0);
    return { row, start, end: start + (row.value / total) * 360 };
  });

  return (
    <svg ref={ref} className="pec-social-reason-chart-svg" viewBox="0 0 860 530" role="img" aria-labelledby="pec-annual-social-reason-pie-title pec-annual-social-reason-pie-description" xmlns="http://www.w3.org/2000/svg">
      <title id="pec-annual-social-reason-pie-title">Incidenza annuale dei Social Reason · {analysis.annualSocialReasonPeriodLabel}</title>
      <desc id="pec-annual-social-reason-pie-description">Distribuzione percentuale annuale di Altro, Copertura, Delivery, Assurance e Vendibilità.</desc>
      <rect width="860" height="530" rx="12" fill="#ffffff" />
      <text data-export-title="true" x="36" y="40" fill="#173249" fontFamily="Inter, Arial, sans-serif" fontSize="18" fontWeight="700">Distribuzione case per tipologia</text>
      <text data-export-title="true" x="36" y="63" fill="#7b8a96" fontFamily="Inter, Arial, sans-serif" fontSize="12">{analysis.annualSocialReasonPeriodLabel} · {formatNumber(analysis.annualSocialReasonTotal)} casi</text>
      {positiveRows.length === 0 ? (
        <circle cx="270" cy="285" r="155" fill="#f2f5f7" stroke="#d9e1e6" strokeWidth="2" />
      ) : segments.length === 1 ? (
        <circle cx="270" cy="285" r="155" fill={ANNUAL_SOCIAL_REASON_COLORS[segments[0].row.key]} />
      ) : segments.map(({ row, start, end }) => (
        <path key={row.key} d={piePath(270, 285, 155, start, end)} fill={ANNUAL_SOCIAL_REASON_COLORS[row.key]} stroke="#ffffff" strokeWidth="2">
          <title>{row.label}: {formatNumber(row.value)}</title>
        </path>
      ))}
      <g transform="translate(505 165)">
        {analysis.annualSocialReasonRows.map((row, index) => (
          <g key={row.key} transform={`translate(0 ${index * 58})`}>
            <circle cx="7" cy="7" r="6" fill={ANNUAL_SOCIAL_REASON_COLORS[row.key]} />
            <text x="24" y="11" fill="#263c4d" fontFamily="Inter, Arial, sans-serif" fontSize="13" fontWeight="700">{row.label}</text>
            <text x="24" y="30" fill="#667987" fontFamily="Inter, Arial, sans-serif" fontSize="11">{formatNumber(row.value)} · {((row.value / total) * 100).toLocaleString('it-IT', { maximumFractionDigits: 1 })}%</text>
          </g>
        ))}
      </g>
    </svg>
  );
});

export function PecSocialSubreasonPanel({ availability, demo }: { availability: ProcessingAvailability | null; demo: boolean }) {
  const svgRef = useRef<SVGSVGElement>(null);
  const territorialSvgRef = useRef<SVGSVGElement>(null);
  const channelPieSvgRef = useRef<SVGSVGElement>(null);
  const channelBarSvgRef = useRef<SVGSVGElement>(null);
  const topicSvgRef = useRef<SVGSVGElement>(null);
  const socialReasonBarSvgRef = useRef<SVGSVGElement>(null);
  const socialReasonPieSvgRef = useRef<SVGSVGElement>(null);
  const annualSocialReasonBarSvgRef = useRef<SVGSVGElement>(null);
  const annualSocialReasonPieSvgRef = useRef<SVGSVGElement>(null);
  const [year, setYear] = useState<number | ''>('');
  const [month, setMonth] = useState<number | ''>('');
  const [analysis, setAnalysis] = useState<PecSocialSubreasonAnalysis | null>(null);
  const [status, setStatus] = useState<LoadStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  const availableYears = outputReportYears(availability, 'pec-social-subreason');
  const selectedYear = year;
  const availableMonths = selectedYear
    ? outputReportMonths(availability, 'pec-social-subreason', selectedYear)
    : [];
  const selectedMonth = month;

  useEffect(() => {
    if (year || availableYears.length === 0) return;
    const initialYear = availableYears[0];
    const timeout = window.setTimeout(() => {
      setYear(initialYear);
      setMonth(outputReportMonths(availability, 'pec-social-subreason', initialYear).at(-1) ?? defaultMonthForYear(initialYear));
    }, 0);
    return () => window.clearTimeout(timeout);
  }, [availability, availableYears, year]);

  const loadAnalysis = useCallback(async (regenerate = false) => {
    if (!selectedYear || !selectedMonth) return;
    setStatus('loading');
    setError(null);
    setExportError(null);
    try {
      const result = demo
        ? {
            ...demoAnalysis(),
            generation: {
              generatedAt: new Date().toISOString(),
              source: regenerate ? 'manual' as const : 'nightly' as const,
              cached: !regenerate,
              version: 1,
            },
          }
        : (await httpsCallable<{ year: number; month: number; regenerate: boolean }, PecSocialSubreasonAnalysis>(
            requireFirebaseFunctions(),
            'getOutputPecSocialSubreasonReport',
            OUTPUT_REPORT_CALLABLE_OPTIONS,
          )({ year: selectedYear, month: selectedMonth, regenerate })).data;
      setAnalysis(result);
      setStatus('ready');
    } catch (cause) {
      setError(errorMessage(cause));
      setStatus('error');
    }
  }, [demo, selectedMonth, selectedYear]);

  useEffect(() => {
    if (!selectedYear || !selectedMonth || status !== 'idle') return;
    const timeout = window.setTimeout(() => void loadAnalysis(false), 0);
    return () => window.clearTimeout(timeout);
  }, [loadAnalysis, selectedMonth, selectedYear, status]);

  const exportSvg = (target: SVGSVGElement | null, prefix: string) => {
    if (!target || !analysis) return;
    setExportError(null);
    const serialized = svgElementMarkupForExport(target);
    downloadBlob(
      new Blob([`<?xml version="1.0" encoding="UTF-8"?>\n${serialized}`], { type: 'image/svg+xml;charset=utf-8' }),
      `${prefix}-${analysis.selectedYear}-${String(analysis.selectedMonth).padStart(2, '0')}.svg`,
    );
  };

  const exportPng = async (target: SVGSVGElement | null, prefix: string) => {
    if (!target || !analysis) return;
    setExportError(null);
    try {
      const serialized = svgElementMarkupForExport(target);
      const image = new Image();
      image.decoding = 'async';
      await new Promise<void>((resolve, reject) => {
        image.onload = () => resolve();
        image.onerror = () => reject(new Error('Immagine non generata'));
        image.src = svgMarkupDataUrl(serialized);
      });
      const canvas = document.createElement('canvas');
      canvas.width = 1920;
      canvas.height = 1000;
      const context = canvas.getContext('2d');
      if (!context) throw new Error('Canvas non disponibile');
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      const png = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png', 1));
      if (!png) throw new Error('PNG non generato');
      downloadBlob(png, `${prefix}-${analysis.selectedYear}-${String(analysis.selectedMonth).padStart(2, '0')}.png`);
    } catch {
      setExportError('Non è stato possibile esportare il grafico in PNG. Puoi usare il formato SVG.');
    }
  };

  const exportExcel = () => {
    if (!analysis) return;
    setExportError(null);
    try {
      const workbook = createPecSocialSubreasonWorkbook(analysis);
      downloadBlob(
        new Blob([Uint8Array.from(workbook).buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
        pecSocialSubreasonWorkbookFileName(analysis.selectedYear, analysis.selectedMonth),
      );
    } catch {
      setExportError('Non è stato possibile creare il file Excel.');
    }
  };

  return (
    <>
      <section className="trend-period-card">
        <div className="trend-period-copy">
          <span><CalendarRange size={22} /></span>
          <div>
            <p>Periodo di analisi</p>
            <h2>Seleziona anno e mese</h2>
            <small>
              {analysis?.generation
                ? `${formatReportGeneration(analysis.generation)}. “Rigenera report” sovrascrive questa versione.`
                : 'Il totale arriva da Case in entrata; i case PEC vengono letti dalle Note del Master.'}
            </small>
          </div>
        </div>
        <div className="trend-period-actions">
          <label>
            <span>Anno</span>
            <select value={selectedYear} disabled={!availability || status === 'loading'} onChange={(event) => {
              const nextYear = Number(event.target.value);
              setYear(nextYear);
              setMonth(outputReportMonths(availability, 'pec-social-subreason', nextYear).at(-1) ?? defaultMonthForYear(nextYear));
              setAnalysis(null);
              setStatus('idle');
            }}>
              {!availableYears.length && <option value="">Caricamento…</option>}
              {availableYears.map((availableYear) => <option value={availableYear} key={availableYear}>{availableYear}</option>)}
            </select>
          </label>
          <label>
            <span>Mese</span>
            <select value={selectedMonth} disabled={!selectedYear || status === 'loading'} onChange={(event) => {
              setMonth(Number(event.target.value));
              setAnalysis(null);
              setStatus('idle');
            }}>
              {!month && <option value="">Seleziona</option>}
              {availableMonths.map((availableMonth) => (
                <option value={availableMonth} key={availableMonth}>{MONTHS[availableMonth - 1]}</option>
              ))}
            </select>
          </label>
          <button className="button button-primary" type="button" onClick={() => void loadAnalysis(analysis !== null)} disabled={!selectedYear || !selectedMonth || status === 'loading'}>
            {status === 'loading' ? <LoaderCircle className="spin" size={17} /> : <MailCheck size={17} />}
            {status === 'loading' ? 'Elaborazione…' : analysis ? 'Rigenera report' : 'Genera report'}
          </button>
        </div>
      </section>

      {status === 'idle' && (
        <section className="trend-empty-state">
          <span><MailCheck size={24} /></span>
          <div><strong>Il report PEC è pronto per essere generato</strong><p>Scegli il periodo per calcolare stato operativo e volumi PEC.</p></div>
        </section>
      )}
      {status === 'loading' && (
        <section className="trend-empty-state loading">
          <span><LoaderCircle className="spin" size={24} /></span>
          <div><strong>Sto calcolando il report PEC</strong><p>Incrocio il totale Case in entrata con le Note del Master del mese selezionato.</p></div>
        </section>
      )}
      {status === 'error' && (
        <section className="trend-empty-state error">
          <span><AlertTriangle size={24} /></span>
          <div><strong>Report PEC non disponibile</strong><p>{error}</p></div>
          <button type="button" className="button button-secondary" onClick={() => void loadAnalysis(false)}><RefreshCw size={16} /> Riprova</button>
        </section>
      )}

      {status === 'ready' && analysis && (
        <>
          <section className="trend-summary-grid pec-summary-grid" aria-label="Sintesi stato operativo PEC">
            <article><span>Totale casi in entrata</span><strong>{formatNumber(analysis.totalIncoming)}</strong><small>{analysis.periodLabel}</small></article>
            <article><span>Chiuse</span><strong>{formatNumber(analysis.closedCases)}</strong><small>totale meno case PEC</small></article>
            <article><span>In lavorazione</span><strong>{formatNumber(analysis.pecCases)}</strong><small>Note con valore PEC</small></article>
            <article><span>Backlog agenzia</span><strong>0</strong><small>come da modello cliente</small></article>
          </section>

          <ReportDataWarnings warnings={analysis.dataWarnings} />

          <section className="pec-report-grid">
            <article className="trend-chart-card pec-chart-card">
              <header>
                <div><p>Grafico 1</p><h2>Stato operativo case</h2><span>Distribuzione mensile secondo il modello del cliente.</span></div>
                <div className="trend-export-actions">
                  <button type="button" className="button button-secondary" onClick={() => void exportPng(svgRef.current, 'social-subreason-pec')}><ImageDown size={15} /> PNG</button>
                  <button type="button" className="button button-secondary" onClick={() => exportSvg(svgRef.current, 'social-subreason-pec')}><FileDown size={15} /> SVG</button>
                </div>
              </header>
              <div className="pec-chart-wrap"><PecOperationalChart analysis={analysis} ref={svgRef} /></div>
            </article>

            <article className="trend-table-card pec-table-card">
              <header>
                <div><p>Tabella 1</p><h2>Stato operativo case</h2><span>{analysis.periodLabel}</span></div>
                <button type="button" className="button button-primary" onClick={exportExcel}><Download size={15} /> Esporta Excel</button>
              </header>
              <div className="pec-table-wrap">
                <table className="pec-table">
                  <thead><tr><th>Mensile</th><th>Slide 4 (Stato operativo case)</th></tr></thead>
                  <tbody>
                    {analysis.rows.map((row) => (
                      <tr key={row.key}><th scope="row">{row.label}</th><td>{formatNumber(row.value)}</td></tr>
                    ))}
                  </tbody>
                  <tfoot><tr><th scope="row">Totale casi in entrata</th><td>{formatNumber(analysis.totalIncoming)}</td></tr></tfoot>
                </table>
              </div>
              <footer><Table2 size={14} /> Chiuse = totale mensile − righe con Note uguale a PEC; il confronto non distingue maiuscole e minuscole.</footer>
            </article>
          </section>

          <section className="pec-report-grid pec-territorial-report-grid">
            <article className="trend-chart-card pec-chart-card">
              <header>
                <div><p>Grafico 2</p><h2>Macroargomenti per area</h2><span>Distribuzione dei case PEC in lavorazione nelle aree territoriali configurate.</span></div>
                <div className="trend-export-actions">
                  <button type="button" className="button button-secondary" onClick={() => void exportPng(territorialSvgRef.current, 'macroargomenti-pec')}><ImageDown size={15} /> PNG</button>
                  <button type="button" className="button button-secondary" onClick={() => exportSvg(territorialSvgRef.current, 'macroargomenti-pec')}><FileDown size={15} /> SVG</button>
                </div>
              </header>
              <div className="pec-chart-wrap"><PecTerritorialChart analysis={analysis} ref={territorialSvgRef} /></div>
            </article>

            <article className="trend-table-card pec-table-card">
              <header>
                <div><p>Tabella 2</p><h2>Distribuzione case per tipologia</h2><span>{analysis.periodLabel}</span></div>
                <button type="button" className="button button-primary" onClick={exportExcel}><Download size={15} /> Esporta Excel</button>
              </header>
              <div className="pec-table-wrap">
                <table className="pec-table pec-territorial-table">
                  <thead><tr><th>Mensile</th><th>Slide 5 (Distribuzione case per tipologia)</th></tr></thead>
                  <tbody>
                    <tr className="pec-section-row"><th scope="row">Macroargomenti</th><td aria-label="intestazione sezione" /></tr>
                    {analysis.territorialRows.map((row) => (
                      <tr key={row.key}><th scope="row">{row.label}</th><td>{formatNumber(row.value)}</td></tr>
                    ))}
                  </tbody>
                  <tfoot><tr><th scope="row">Totale PEC distribuite</th><td>{formatNumber(analysis.territorialResolvedCases)}</td></tr></tfoot>
                </table>
              </div>
              <footer><Table2 size={14} /> Le aree rispettano la configurazione salvata in Impostazioni → Aree territoriali.</footer>
            </article>
          </section>

          {analysis.territorialIssues.length > 0 && (
            <div className="trend-export-error" role="alert">
              <AlertTriangle size={15} />
              {formatNumber(analysis.territorialIssues.reduce((total, issue) => total + issue.count, 0))} case PEC non distribuite: città non risolta o regione non assegnata.
            </div>
          )}

          <section className="pec-channel-report" aria-labelledby="pec-channel-report-title">
            <article className="trend-table-card pec-table-card pec-channel-table-card">
              <header>
                <div><p>Tabella 3</p><h2 id="pec-channel-report-title">Canali di ingresso case</h2><span>{analysis.periodLabel}</span></div>
                <button type="button" className="button button-primary" onClick={exportExcel}><Download size={15} /> Esporta Excel</button>
              </header>
              <div className="pec-table-wrap">
                <table className="pec-table pec-channel-table">
                  <thead>
                    <tr><th>Mensile</th><th colSpan={3}>Slide 6 (Canali di ingresso case)</th></tr>
                    <tr><th aria-label="Canale" /><th>Casi chiusi</th><th>In lavorazione</th><th>Totale</th></tr>
                  </thead>
                  <tbody>
                    {analysis.channelRows.map((row) => (
                      <tr key={row.key}><th scope="row">{row.label}</th><td>{formatNumber(row.closed)}</td><td>{formatNumber(row.working)}</td><td>{formatNumber(row.total)}</td></tr>
                    ))}
                  </tbody>
                  <tfoot><tr><th scope="row">Totale</th><td>{formatNumber(analysis.channelTotals.closed)}</td><td>{formatNumber(analysis.channelTotals.working)}</td><td>{formatNumber(analysis.channelTotals.total)}</td></tr></tfoot>
                </table>
              </div>
              <footer><Table2 size={14} /> Web dal Master mensile; Facebook, LinkedIn e X dal Master mensile, con l’aggiunta del Mail nei giorni 1–{analysis.channelCutoffDay}.</footer>
            </article>

            <div className="pec-channel-chart-grid">
              <article className="trend-chart-card pec-chart-card">
                <header>
                  <div><p>Grafico 3</p><h2>Distribuzione per canale</h2><span>Incidenza di Web, Facebook, LinkedIn e X sul totale mensile.</span></div>
                  <div className="trend-export-actions">
                    <button type="button" className="button button-secondary" onClick={() => void exportPng(channelPieSvgRef.current, 'canali-ingresso-distribuzione')}><ImageDown size={15} /> PNG</button>
                    <button type="button" className="button button-secondary" onClick={() => exportSvg(channelPieSvgRef.current, 'canali-ingresso-distribuzione')}><FileDown size={15} /> SVG</button>
                  </div>
                </header>
                <div className="pec-channel-chart-wrap"><PecChannelPieChart analysis={analysis} ref={channelPieSvgRef} /></div>
              </article>

              <article className="trend-chart-card pec-chart-card">
                <header>
                  <div><p>Grafico 4</p><h2>Stato per canale</h2><span>Confronto tra casi chiusi e in lavorazione.</span></div>
                  <div className="trend-export-actions">
                    <button type="button" className="button button-secondary" onClick={() => void exportPng(channelBarSvgRef.current, 'canali-ingresso-stato')}><ImageDown size={15} /> PNG</button>
                    <button type="button" className="button button-secondary" onClick={() => exportSvg(channelBarSvgRef.current, 'canali-ingresso-stato')}><FileDown size={15} /> SVG</button>
                  </div>
                </header>
                <div className="pec-channel-chart-wrap"><PecChannelBarChart analysis={analysis} ref={channelBarSvgRef} /></div>
              </article>
            </div>
          </section>

          <section className="pec-topic-report-grid" aria-labelledby="pec-topic-report-title">
            <article className="trend-table-card pec-table-card pec-topic-table-card">
              <header>
                <div><p>Tabella 4</p><h2 id="pec-topic-report-title">Distribuzione case per tipologia</h2><span>{analysis.periodLabel}</span></div>
                <button type="button" className="button button-primary" onClick={exportExcel}><Download size={15} /> Esporta Excel</button>
              </header>
              <div className="pec-table-wrap">
                <table className="pec-table pec-topic-table">
                  <thead><tr><th>Mensile</th><th>Slide 5 (Distribuzione case per tipologia)</th></tr></thead>
                  <tbody>
                    <tr className="pec-section-row"><th scope="row">Macroargomenti</th><td aria-label="intestazione sezione" /></tr>
                    {analysis.topicRows.map((row) => (
                      <tr key={row.key}><th scope="row">{row.label}</th><td>{formatNumber(row.value)}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <footer><Table2 size={14} /> Conteggio del Master nel mese selezionato, raggruppato per il valore della colonna Argomento.</footer>
            </article>

            <article className="trend-chart-card pec-chart-card">
              <header>
                <div><p>Grafico 5</p><h2>Distribuzione per macroargomento</h2><span>Incidenza delle quattro tipologie nel mese selezionato.</span></div>
                <div className="trend-export-actions">
                  <button type="button" className="button button-secondary" onClick={() => void exportPng(topicSvgRef.current, 'distribuzione-case-tipologia')}><ImageDown size={15} /> PNG</button>
                  <button type="button" className="button button-secondary" onClick={() => exportSvg(topicSvgRef.current, 'distribuzione-case-tipologia')}><FileDown size={15} /> SVG</button>
                </div>
              </header>
              <div className="pec-topic-chart-wrap"><PecTopicChart analysis={analysis} ref={topicSvgRef} /></div>
            </article>
          </section>

          <section className="pec-social-reason-report-grid" aria-labelledby="pec-social-reason-report-title">
            <article className="trend-table-card pec-table-card pec-social-reason-table-card">
              <header>
                <div><p>Tabella 5</p><h2 id="pec-social-reason-report-title">Distribuzione per Social Reason</h2><span>{analysis.periodLabel}</span></div>
                <button type="button" className="button button-primary" onClick={exportExcel}><Download size={15} /> Esporta Excel</button>
              </header>
              <div className="pec-table-wrap">
                <table className="pec-table pec-social-reason-table">
                  <thead>
                    <tr><th>Mensile</th><th>Slide 5 (Distribuzione case per tipologia)</th></tr>
                    <tr><th>Argomenti</th><th>Richieste</th></tr>
                  </thead>
                  <tbody>
                    {analysis.socialReasonRows.map((row) => (
                      <tr key={row.key}><th scope="row">{row.label}</th><td>{formatNumber(row.value)}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <footer><Table2 size={14} /> Il valore Altro viene distinto tramite Argomento in “Altro - Info generali” e “Altro - Problemi”.</footer>
            </article>

            <article className="trend-chart-card pec-chart-card">
              <header>
                <div><p>Grafico 6</p><h2>Volumi per Social Reason</h2><span>Confronto delle richieste nel mese selezionato.</span></div>
                <div className="trend-export-actions">
                  <button type="button" className="button button-secondary" onClick={() => void exportPng(socialReasonBarSvgRef.current, 'social-reason-volumi')}><ImageDown size={15} /> PNG</button>
                  <button type="button" className="button button-secondary" onClick={() => exportSvg(socialReasonBarSvgRef.current, 'social-reason-volumi')}><FileDown size={15} /> SVG</button>
                </div>
              </header>
              <div className="pec-social-reason-chart-wrap"><PecSocialReasonBarChart analysis={analysis} ref={socialReasonBarSvgRef} /></div>
            </article>

            <article className="trend-chart-card pec-chart-card">
              <header>
                <div><p>Grafico 7</p><h2>Incidenza per Social Reason</h2><span>Distribuzione percentuale delle richieste classificate.</span></div>
                <div className="trend-export-actions">
                  <button type="button" className="button button-secondary" onClick={() => void exportPng(socialReasonPieSvgRef.current, 'social-reason-distribuzione')}><ImageDown size={15} /> PNG</button>
                  <button type="button" className="button button-secondary" onClick={() => exportSvg(socialReasonPieSvgRef.current, 'social-reason-distribuzione')}><FileDown size={15} /> SVG</button>
                </div>
              </header>
              <div className="pec-social-reason-chart-wrap"><PecSocialReasonPieChart analysis={analysis} ref={socialReasonPieSvgRef} /></div>
            </article>
          </section>

          <section className="pec-social-reason-report-grid pec-annual-social-reason-report-grid" aria-labelledby="pec-annual-social-reason-report-title">
            <article className="trend-table-card pec-table-card pec-social-reason-table-card">
              <header>
                <div><p>Tabella 6</p><h2 id="pec-annual-social-reason-report-title">Distribuzione annuale per Social Reason</h2><span>{analysis.annualSocialReasonPeriodLabel}</span></div>
                <button type="button" className="button button-primary" onClick={exportExcel}><Download size={15} /> Esporta Excel</button>
              </header>
              <div className="pec-table-wrap">
                <table className="pec-table pec-social-reason-table">
                  <thead>
                    <tr><th>Annuale</th><th>Slide 8 (Distribuzione case per tipologia)</th></tr>
                    <tr><th>Social Reason</th><th>Totale</th></tr>
                  </thead>
                  <tbody>
                    {analysis.annualSocialReasonRows.map((row) => (
                      <tr key={row.key}><th scope="row">{row.label}</th><td>{formatNumber(row.value)}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <footer><Table2 size={14} /> Tutte le date da {analysis.annualSocialReasonPeriodLabel}; Altro resta una categoria unica.</footer>
            </article>

            <article className="trend-chart-card pec-chart-card">
              <header>
                <div><p>Grafico 8</p><h2>Volumi annuali per Social Reason</h2><span>{analysis.annualSocialReasonPeriodLabel}</span></div>
                <div className="trend-export-actions">
                  <button type="button" className="button button-secondary" onClick={() => void exportPng(annualSocialReasonBarSvgRef.current, 'social-reason-annuale-volumi')}><ImageDown size={15} /> PNG</button>
                  <button type="button" className="button button-secondary" onClick={() => exportSvg(annualSocialReasonBarSvgRef.current, 'social-reason-annuale-volumi')}><FileDown size={15} /> SVG</button>
                </div>
              </header>
              <div className="pec-social-reason-chart-wrap"><PecAnnualSocialReasonBarChart analysis={analysis} ref={annualSocialReasonBarSvgRef} /></div>
            </article>

            <article className="trend-chart-card pec-chart-card">
              <header>
                <div><p>Grafico 9</p><h2>Incidenza annuale per Social Reason</h2><span>{analysis.annualSocialReasonPeriodLabel}</span></div>
                <div className="trend-export-actions">
                  <button type="button" className="button button-secondary" onClick={() => void exportPng(annualSocialReasonPieSvgRef.current, 'social-reason-annuale-distribuzione')}><ImageDown size={15} /> PNG</button>
                  <button type="button" className="button button-secondary" onClick={() => exportSvg(annualSocialReasonPieSvgRef.current, 'social-reason-annuale-distribuzione')}><FileDown size={15} /> SVG</button>
                </div>
              </header>
              <div className="pec-social-reason-chart-wrap"><PecAnnualSocialReasonPieChart analysis={analysis} ref={annualSocialReasonPieSvgRef} /></div>
            </article>
          </section>

          <div className="pec-method-note">
            <BarChart3 size={16} />
            <span>Fonti mensili: <strong>{analysis.masterFile.name}</strong> e <strong>{analysis.mailFile.name}</strong>. Per i canali social il Master copre l’intero mese e il Mail integra i giorni 1–{analysis.channelCutoffDay}. L’annuale usa <strong>{analysis.annualMasterFiles.map(({ name }) => name).join(' e ')}</strong>.</span>
          </div>
          {exportError && <div className="trend-export-error" role="alert"><AlertTriangle size={15} /> {exportError}</div>}
        </>
      )}
    </>
  );
}
